import * as mentorApi from '../../api/mentorApi';
import { mergeRosterStudents, rosterHasStudents } from '../../utils/studentResolution';
import { computeBootPartial, createEmptyBootResult } from '../bootContract';
import { makeFetchSlice, sliceError, sliceLoading, sliceSuccess } from '../fetchSlice';
import { rosterKey } from '../ids';
import { normalizeCohortEntity } from '../normalize/cohort';
import { normalizeEvaluationEntity } from '../normalize/evaluation';
import { normalizeRosterStudents } from '../normalize/student';
import { normalizeSessionEntity } from '../normalize/session';
import { titleCaseWord } from '../normalize/helpers';
import { WORKSPACE_ACTIONS } from '../workspaceReducer';

// Each roster request makes mentor-api call Central, so prefetching every school at once bursts Central's database.
export const ROSTER_PREFETCH_CONCURRENCY = 2;

/**
 * Runs worker over items with at most `limit` in flight. Resolves to Promise.allSettled-shaped results in item order.
 */
export async function settleWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let next = 0;
  const runner = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      try {
        results[index] = { status: 'fulfilled', value: await worker(items[index], index) };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };
  // allSettled, not Promise.all: boot code must never reject as a whole (scripts/check-boot-safety.js).
  await Promise.allSettled(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, runner));
  return results;
}

/**
 * The school/branch pairs worth a roster request at boot. A pair with no branch is never requested: Central answers
 * 400 for it (435 failed boot requests in the 2026-10 production log), so the request only added an error and a
 * warning. Pairs already loaded at login (a scoped user's own school) are skipped too, so they are not fetched twice.
 */
export function prefetchableRosterPairs(pairs, loadedRosterKeys = []) {
  const loaded = new Set(loadedRosterKeys);
  return pairs.filter(({ schoolId, branchId }) => branchId && !loaded.has(rosterKey(schoolId, branchId)));
}

function canFetchCohorts(role) {
  return role === 'program_administrator' || role === 'spoc' || role === 'auditor';
}

export function buildSessionAuditorIndex(sessions = []) {
  const index = {};
  sessions.forEach(session => {
    (session.auditorAssignments || []).forEach(assignment => {
      if (!assignment.id) return;
      index[String(assignment.id)] = {
        sessionId: session.id,
        sessionName: `${session.sessionType || ''} - ${session.cohortName || ''}`.trim(),
        school: session.schoolName || '',
        auditorName: assignment.auditorName || '',
      };
    });
  });
  return index;
}

export function mergeReopenResponsesIntoReports(reports = [], sessionId, reopenedRows = []) {
  const reopenedById = {};
  reopenedRows.forEach(row => {
    if (row?.id) reopenedById[String(row.id)] = row;
  });

  return reports.map(rep => {
    if (String(rep.sessionId) !== String(sessionId)) return rep;
    const apiRow = reopenedById[String(rep.id)];
    if (!apiRow) {
      return {
        ...rep,
        status: 'draft',
        rejectionReason: '',
        approvedBy: '',
        approvedTime: '',
      };
    }
    return {
      ...rep,
      status: apiRow.status || 'draft',
      isRevision: apiRow.isRevision ?? false,
      revisionNumber: apiRow.revisionNumber ?? 1,
      rejectionReason: '',
      approvedBy: '',
      approvedTime: '',
    };
  });
}

export function buildReportsFromEvaluations(evaluations = [], sessions = []) {
  const sessionAuditorIndex = buildSessionAuditorIndex(sessions);
  const sessionById = {};
  sessions.forEach(session => {
    if (session?.id) sessionById[String(session.id)] = session;
  });

  return evaluations.map(ev => {
    const sessionMeta = sessionAuditorIndex[String(ev.sessionAuditorId)] || {};
    const session = sessionById[String(ev.sessionId)] || null;
    const fallbackSessionName = session
      ? `${session.sessionType || ''} - ${session.cohortName || ''}`.trim()
      : '';

    return {
      id: ev.id,
      sessionId: ev.sessionId || sessionMeta.sessionId || '',
      studentId: ev.studentId || '',
      role: ev.role || '',
      title: `${ev.studentName} (${titleCaseWord(ev.role) || 'Delegate'})`,
      studentName: ev.studentName,
      roleBadge: titleCaseWord(ev.role) || 'Delegate',
      sessionName: sessionMeta.sessionName || fallbackSessionName,
      auditorName: ev.auditorName || sessionMeta.auditorName || '',
      school: sessionMeta.school || session?.schoolName || '',
      status: ev.status,
      submittedAt: ev.submittedAt || ev.approvedAt || '',
      keywords: ev.positiveKeywords || [],
      coachingKeywords: ev.negativeKeywords || [],
      rating: ev.rating ?? '',
      comment: ev.comment || '',
      rejectionReason: ev.rejectionReason || '',
      approvedBy: ev.approvedBy || '',
      approvedTime: ev.approvedAt || '',
      isRevision: ev.isRevision || false,
      revisionNumber: ev.revisionNumber || 1,
    };
  });
}

function buildBootReportsNext(evaluations) {
  return buildReportsFromEvaluations(evaluations, []);
}

export async function fetchEvaluationsReports(sessions = []) {
  const evaluations = await mentorApi.getEvaluations();
  return buildReportsFromEvaluations(evaluations, sessions);
}

function buildGroupsBySchool(allGroupsList) {
  const groupsBySchool = {};
  allGroupsList.forEach(group => {
    const sid = String(group.schoolId);
    if (!groupsBySchool[sid]) groupsBySchool[sid] = [];
    groupsBySchool[sid].push({
      ...group,
      id: String(group.id),
      schoolId: sid,
      cohortIds: (group.cohortIds || group.cohort_ids || []).map(String),
      year: group.cohorts?.[0]?.levelYear ?? group.cohorts?.[0]?.level_year ?? null,
    });
  });
  return groupsBySchool;
}

function buildRostersFromSessions({ user, normalizedSessions, normalizedCohortEntities }) {
  const cohortMap = {};
  normalizedCohortEntities.forEach(c => { cohortMap[c.id] = c; });

  const schoolPairs = {};
  const registerSchoolPair = (schoolId, branchId) => {
    if (!schoolId) return;
    const key = rosterKey(schoolId, branchId);
    if (!schoolPairs[key]) {
      schoolPairs[key] = { schoolId: String(schoolId), branchId: branchId ? String(branchId) : null };
    }
  };

  normalizedSessions.forEach(session => {
    if (session.schoolId) {
      registerSchoolPair(session.schoolId, session.branchId);
      return;
    }
    const cohort = cohortMap[String(session.cohortId)];
    if (cohort?.schoolId) registerSchoolPair(cohort.schoolId, cohort.branchId);
  });
  if (user?.apiUser?.school_id) {
    registerSchoolPair(user.apiUser.school_id, user.apiUser.branch_id);
  }

  return Object.values(schoolPairs);
}

async function runWorkspaceBoot({ user, formatApiError, addWarning, skipRosterPrefetch = false, loadedRosterKeys = [] }) {
  const role = user?.role;
  const loadsEvaluations = role === 'program_administrator' || role === 'counselor' || role === 'auditor';
  const fetchCohorts = canFetchCohorts(role);

  const cohortPromise = fetchCohorts
    ? mentorApi.getCohorts()
    : Promise.resolve([]);
  const sessionPromise = mentorApi.getSessions();
  const evalPromise = loadsEvaluations
    ? mentorApi.getEvaluations()
    : Promise.resolve([]);

  const [cohortResult, sessionResult, evalResult] = await Promise.allSettled([
    cohortPromise,
    sessionPromise,
    evalPromise,
  ]);

  const loadingCohorts = sliceLoading(makeFetchSlice([]));
  const cohortFetch = cohortResult.status === 'fulfilled'
    ? sliceSuccess(cohortResult.value, loadingCohorts)
    : sliceError(
        cohortResult.reason,
        loadingCohorts,
        'Could not load cohorts.',
        formatApiError(cohortResult.reason, 'Loading cohorts')
      );

  const loadingSessions = sliceLoading(makeFetchSlice([]));
  const sessionsFetch = sessionResult.status === 'fulfilled'
    ? sliceSuccess(sessionResult.value, loadingSessions)
    : sliceError(
        sessionResult.reason,
        loadingSessions,
        'Could not load sessions.',
        formatApiError(sessionResult.reason, 'Loading sessions')
      );

  let evaluationsFetch = null;
  let evaluations = [];
  if (loadsEvaluations) {
    const loadingEvaluations = sliceLoading(makeFetchSlice([]));
    evaluationsFetch = evalResult.status === 'fulfilled'
      ? sliceSuccess(evalResult.value, loadingEvaluations)
      : sliceError(
          evalResult.reason,
          loadingEvaluations,
          'Could not load evaluations.',
          formatApiError(evalResult.reason, 'Loading evaluations')
        );
    evaluations = evaluationsFetch.data;
  }

  if (fetchCohorts && cohortResult.status === 'rejected') {
    console.error('Failed to load cohorts:', cohortResult.reason);
    addWarning('cohorts_list', 'Could not load cohort list.', formatApiError(cohortResult.reason, 'Loading cohorts'));
  }
  if (sessionResult.status === 'rejected') {
    console.error('Failed to load sessions:', sessionResult.reason);
    addWarning('sessions_list', 'Could not load session list.', formatApiError(sessionResult.reason, 'Loading sessions'));
  }
  if (evalResult.status === 'rejected') {
    console.error('Failed to load evaluations:', evalResult.reason);
    addWarning('evaluations_list', 'Could not load evaluations.', formatApiError(evalResult.reason, 'Loading evaluations'));
  }

  const cohortSummaries = Array.isArray(cohortFetch.data) ? cohortFetch.data : [];
  const sessionSummaries = Array.isArray(sessionsFetch.data) ? sessionsFetch.data : [];

  const normalizedCohortEntities = cohortSummaries
    .map((cohort) => normalizeCohortEntity(cohort))
    .filter(Boolean);
  const schoolCohortsMapNext = {};
  normalizedCohortEntities.forEach((normalized) => {
    if (!schoolCohortsMapNext[normalized.schoolId]) {
      schoolCohortsMapNext[normalized.schoolId] = [];
    }
    schoolCohortsMapNext[normalized.schoolId].push(normalized);
  });

  const canFetchGroups = role === 'program_administrator' || role === 'spoc';
  let allGroupsList = [];
  if (canFetchGroups) {
    try {
      allGroupsList = await mentorApi.getGroups();
    } catch (err) {
      console.warn('Failed to load groups:', err);
      addWarning('groups_fetch', 'Could not load planning groups.', formatApiError(err, 'Loading groups'));
    }
  }
  const groupsBySchool = {};
  allGroupsList.forEach(group => {
    const sid = String(group.schoolId);
    if (!groupsBySchool[sid]) groupsBySchool[sid] = [];
    groupsBySchool[sid].push({
      ...group,
      id: String(group.id),
      schoolId: sid,
      cohortIds: group.cohortIds.map(String),
      year: group.cohorts?.[0]?.levelYear ?? null,
    });
  });

  const normalizedSessions = sessionSummaries
    .map((session) => normalizeSessionEntity(session))
    .filter(Boolean);
  const sessionAuditorIndex = {};
  normalizedSessions.forEach((normalized) => {
    (normalized.auditorAssignments || []).forEach(assignment => {
      if (!assignment.id) return;
      sessionAuditorIndex[assignment.id] = {
        sessionId: normalized.id,
        sessionName: `${normalized.sessionType} - ${normalized.cohortName}`,
        school: normalized.schoolName || '',
        auditorName: assignment.auditorName || '',
      };
    });
  });

  const cohortMap = {};
  normalizedCohortEntities.forEach(c => { cohortMap[c.id] = c; });

  const schoolPairs = {};
  const registerSchoolPair = (schoolId, branchId) => {
    if (!schoolId) return;
    const key = rosterKey(schoolId, branchId);
    if (!schoolPairs[key]) {
      schoolPairs[key] = { schoolId: String(schoolId), branchId: branchId ? String(branchId) : null };
    }
  };

  normalizedSessions.forEach(session => {
    if (session.schoolId) {
      registerSchoolPair(session.schoolId, session.branchId);
      return;
    }
    const cohort = cohortMap[String(session.cohortId)];
    if (cohort?.schoolId) registerSchoolPair(cohort.schoolId, cohort.branchId);
  });
  if (user?.apiUser?.school_id) {
    registerSchoolPair(user.apiUser.school_id, user.apiUser.branch_id);
  }

  const rosterResults = skipRosterPrefetch
    ? []
    : await settleWithConcurrency(
      // Object.values(schoolPairs),  // before: every pair, including ones without a branch and ones already loaded
      prefetchableRosterPairs(Object.values(schoolPairs), loadedRosterKeys),
      ROSTER_PREFETCH_CONCURRENCY,
      async ({ schoolId, branchId }) => {
        const students = await mentorApi.getStudents(schoolId, branchId);
        return { schoolId, branchId, students };
      }
    );

  const rosters = {};
  const rosterUpdates = [];
  let rosterFailures = 0;
  rosterResults.forEach(result => {
    if (result.status !== 'fulfilled') {
      rosterFailures += 1;
      console.warn('Failed to prefetch students:', result.reason);
      return;
    }
    const { schoolId, branchId, students } = result.value;
    const mapped = normalizeRosterStudents(students, schoolId, branchId);
    rosterUpdates.push({ schoolId, branchId, students: mapped });
    rosters[rosterKey(schoolId, branchId)] = mapped;
  });
  if (rosterFailures > 0) {
    addWarning(
      'roster_prefetch',
      `Could not prefetch students for ${rosterFailures} school roster(s).`,
      null
    );
  }

  const normalizedEvaluations = evaluations
    .map(normalizeEvaluationEntity)
    .filter(Boolean);

  const reportsNext = buildReportsFromEvaluations(evaluations, normalizedSessions);

  return {
    loadsEvaluations,
    cohortFetch,
    sessionsFetch,
    evaluationsFetch,
    schoolCohortsMapNext,
    groupsBySchool,
    normalizedSessions,
    normalizedCohortEntities,
    normalizedEvaluations,
    rosters,
    rosterUpdates,
    reportsNext,
    cohortSummaries,
    sessionSummaries,
  };
}

async function runWorkspaceBootPhase1({ user, formatApiError, addWarning }) {
  const role = user?.role;
  const loadsEvaluations = role === 'program_administrator' || role === 'counselor' || role === 'auditor';
  const fetchCohorts = canFetchCohorts(role);

  const cohortPromise = fetchCohorts
    ? mentorApi.getCohorts()
    : Promise.resolve([]);
  const sessionPromise = mentorApi.getSessions();
  const evalPromise = loadsEvaluations
    ? mentorApi.getEvaluations()
    : Promise.resolve([]);

  const [cohortResult, sessionResult, evalResult] = await Promise.allSettled([
    cohortPromise,
    sessionPromise,
    evalPromise,
  ]);

  const loadingCohorts = sliceLoading(makeFetchSlice([]));
  const cohortFetch = cohortResult.status === 'fulfilled'
    ? sliceSuccess(cohortResult.value, loadingCohorts)
    : sliceError(
        cohortResult.reason,
        loadingCohorts,
        'Could not load cohorts.',
        formatApiError(cohortResult.reason, 'Loading cohorts')
      );

  const loadingSessions = sliceLoading(makeFetchSlice([]));
  const sessionsFetch = sessionResult.status === 'fulfilled'
    ? sliceSuccess(sessionResult.value, loadingSessions)
    : sliceError(
        sessionResult.reason,
        loadingSessions,
        'Could not load sessions.',
        formatApiError(sessionResult.reason, 'Loading sessions')
      );

  let evaluationsFetch = null;
  let evaluations = [];
  if (loadsEvaluations) {
    const loadingEvaluations = sliceLoading(makeFetchSlice([]));
    evaluationsFetch = evalResult.status === 'fulfilled'
      ? sliceSuccess(evalResult.value, loadingEvaluations)
      : sliceError(
          evalResult.reason,
          loadingEvaluations,
          'Could not load evaluations.',
          formatApiError(evalResult.reason, 'Loading evaluations')
        );
    evaluations = evaluationsFetch.data;
  }

  if (fetchCohorts && cohortResult.status === 'rejected') {
    console.error('Failed to load cohorts:', cohortResult.reason);
    addWarning('cohorts_list', 'Could not load cohort list.', formatApiError(cohortResult.reason, 'Loading cohorts'));
  }
  if (sessionResult.status === 'rejected') {
    console.error('Failed to load sessions:', sessionResult.reason);
    addWarning('sessions_list', 'Could not load session list.', formatApiError(sessionResult.reason, 'Loading sessions'));
  }
  if (evalResult.status === 'rejected') {
    console.error('Failed to load evaluations:', evalResult.reason);
    addWarning('evaluations_list', 'Could not load evaluations.', formatApiError(evalResult.reason, 'Loading evaluations'));
  }

  const cohortSummaries = Array.isArray(cohortFetch.data) ? cohortFetch.data : [];
  const sessionSummaries = Array.isArray(sessionsFetch.data) ? sessionsFetch.data : [];
  const reportsNext = buildBootReportsNext(evaluations);

  return {
    loadsEvaluations,
    cohortFetch,
    sessionsFetch,
    evaluationsFetch,
    reportsNext,
    cohortSummaries,
    sessionSummaries,
    evaluations,
    bootWarnings: [],
    partial: computeBootPartial([], {
      cohortFetch,
      sessionsFetch,
      evaluationsFetch,
      loadsEvaluations,
    }),
  };
}

async function runWorkspaceBootPhase2({
  user,
  formatApiError,
  addWarning,
  cohortSummaries,
  sessionSummaries,
  evaluations,
  skipRosterPrefetch = false,
  loadedRosterKeys = [],
}) {
  const normalizedCohortEntities = cohortSummaries
    .map((cohort) => normalizeCohortEntity(cohort))
    .filter(Boolean);
  const schoolCohortsMapNext = {};
  normalizedCohortEntities.forEach((normalized) => {
    if (!schoolCohortsMapNext[normalized.schoolId]) {
      schoolCohortsMapNext[normalized.schoolId] = [];
    }
    schoolCohortsMapNext[normalized.schoolId].push(normalized);
  });

  const canFetchGroups = user?.role === 'program_administrator' || user?.role === 'spoc';
  let allGroupsList = [];
  if (canFetchGroups) {
    try {
      allGroupsList = await mentorApi.getGroups();
    } catch (err) {
      console.warn('Failed to load groups:', err);
      addWarning('groups_fetch', 'Could not load planning groups.', formatApiError(err, 'Loading groups'));
    }
  }
  const groupsBySchool = buildGroupsBySchool(allGroupsList);

  const normalizedSessions = sessionSummaries
    .map((session) => normalizeSessionEntity(session))
    .filter(Boolean);
  const sessionAuditorIndex = {};
  normalizedSessions.forEach((normalized) => {
    (normalized.auditorAssignments || []).forEach(assignment => {
      if (!assignment.id) return;
      sessionAuditorIndex[assignment.id] = {
        sessionId: normalized.id,
        sessionName: `${normalized.sessionType} - ${normalized.cohortName}`,
        school: normalized.schoolName || '',
        auditorName: assignment.auditorName || '',
      };
    });
  });

  const rosterResults = skipRosterPrefetch
    ? []
    : await settleWithConcurrency(
      // buildRostersFromSessions({ user, normalizedSessions, normalizedCohortEntities }),  // before: every pair
      prefetchableRosterPairs(
        buildRostersFromSessions({ user, normalizedSessions, normalizedCohortEntities }),
        loadedRosterKeys
      ),
      ROSTER_PREFETCH_CONCURRENCY,
      async ({ schoolId, branchId }) => {
        const students = await mentorApi.getStudents(schoolId, branchId);
        return { schoolId, branchId, students };
      }
    );

  const rosters = {};
  const rosterUpdates = [];
  let rosterFailures = 0;
  rosterResults.forEach(result => {
    if (result.status !== 'fulfilled') {
      rosterFailures += 1;
      console.warn('Failed to prefetch students:', result.reason);
      return;
    }
    const { schoolId, branchId, students } = result.value;
    const mapped = normalizeRosterStudents(students, schoolId, branchId);
    rosterUpdates.push({ schoolId, branchId, students: mapped });
    rosters[rosterKey(schoolId, branchId)] = mapped;
  });
  if (rosterFailures > 0) {
    addWarning(
      'roster_prefetch',
      `Could not prefetch students for ${rosterFailures} school roster(s).`,
      null
    );
  }

  const normalizedEvaluations = evaluations
    .map(normalizeEvaluationEntity)
    .filter(Boolean);

  const reportsNext = buildReportsFromEvaluations(evaluations, normalizedSessions);

  return {
    normalizedSessions,
    normalizedCohortEntities,
    normalizedEvaluations,
    groupsBySchool,
    rosterUpdates,
    rosters,
    reportsNext,
    bootWarnings: [],
    partial: false,
    schoolCohortsMapNext,
    sessionAuditorIndex,
  };
}

/**
 * Fetches and normalizes workspace boot data. Always resolves; never throws.
 * Returns bootWarnings + partial flag for degraded UI.
 */
export async function fetchWorkspaceBoot({ user, formatApiError, skipRosterPrefetch = false, loadedRosterKeys = [] }) {
  const startedAt = typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
  const bootWarnings = [];
  const addWarning = (code, message, detail = null) => {
    bootWarnings.push({ code, message, detail });
  };
  const loadsEvaluations = user?.role === 'program_administrator'
    || user?.role === 'counselor'
    || user?.role === 'auditor';

  const withDuration = (result) => {
    const endedAt = typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
    const durationMs = endedAt - startedAt;
    return { ...result, durationMs };
  };

  try {
    const result = await runWorkspaceBoot({ user, formatApiError, addWarning, skipRosterPrefetch, loadedRosterKeys });
    return withDuration({
      ...result,
      bootWarnings,
      partial: computeBootPartial(bootWarnings, {
        cohortFetch: result.cohortFetch,
        sessionsFetch: result.sessionsFetch,
        evaluationsFetch: result.evaluationsFetch,
        loadsEvaluations,
      }),
      fatal: false,
    });
  } catch (err) {
    console.error('fetchWorkspaceBoot fatal:', err);
    addWarning('boot_fatal', 'Workspace data could not be loaded.', err?.message || String(err));
    return withDuration(createEmptyBootResult(bootWarnings, { loadsEvaluations }));
  }
}

export async function fetchWorkspaceBootStreaming({ user, formatApiError, onPhase1, onPhase2, skipRosterPrefetch = false, loadedRosterKeys = [] }) {
  const startedAt = typeof performance !== 'undefined' && performance.now
    ? performance.now()
    : Date.now();
  const bootWarnings = [];
  const addWarning = (code, message, detail = null) => {
    bootWarnings.push({ code, message, detail });
  };

  try {
    const phase1 = await runWorkspaceBootPhase1({ user, formatApiError, addWarning });
    const phase1Payload = {
      ...phase1,
      bootWarnings: [...bootWarnings],
      partial: computeBootPartial(bootWarnings, {
        cohortFetch: phase1.cohortFetch,
        sessionsFetch: phase1.sessionsFetch,
        evaluationsFetch: phase1.evaluationsFetch,
        loadsEvaluations: phase1.loadsEvaluations,
      }),
      fatal: false,
    };

    if (onPhase1) {
      onPhase1(phase1Payload);
    }

    let phase2;
    try {
      phase2 = await runWorkspaceBootPhase2({
        user,
        formatApiError,
        addWarning,
        cohortSummaries: phase1.cohortSummaries,
        sessionSummaries: phase1.sessionSummaries,
        evaluations: phase1.evaluations,
        skipRosterPrefetch,
        loadedRosterKeys,
      });
    } catch (err) {
      console.error('fetchWorkspaceBootStreaming phase2 fatal:', err);
      addWarning(
        'boot_phase2',
        'Workspace enrichment could not be completed.',
        err?.message || String(err)
      );
      phase2 = {
        loadsEvaluations: phase1.loadsEvaluations,
        normalizedSessions: [],
        normalizedCohortEntities: [],
        normalizedEvaluations: [],
        groupsBySchool: {},
        rosterUpdates: [],
        rosters: {},
        reportsNext: phase1.reportsNext,
        bootWarnings: [...bootWarnings],
        partial: true,
        schoolCohortsMapNext: {},
      };
    }

    const endedAt = typeof performance !== 'undefined' && performance.now
      ? performance.now()
      : Date.now();
    const durationMs = endedAt - startedAt;
    const phase2Payload = {
      ...phase2,
      bootWarnings: [...bootWarnings],
      durationMs,
      fatal: false,
      partial: bootWarnings.length > 0 || phase2.partial,
    };
    if (onPhase2) {
      onPhase2(phase2Payload);
    }
    return {
      ...phase1Payload,
      ...phase2Payload,
      bootWarnings: phase2Payload.bootWarnings,
      durationMs,
    };
  } catch (err) {
    console.error('fetchWorkspaceBootStreaming fatal:', err);
    throw err;
  }
}

/**
 * Writes boot payloads into workspace store (dual-write companion for legacy useState).
 */
export function applyWorkspaceBoot(dispatch, bootData, existingRosters = {}) {
  dispatch({ type: WORKSPACE_ACTIONS.SET_FETCH_COHORTS, payload: bootData.cohortFetch });
  dispatch({ type: WORKSPACE_ACTIONS.SET_FETCH_SESSIONS, payload: bootData.sessionsFetch });
  if (bootData.loadsEvaluations && bootData.evaluationsFetch) {
    dispatch({ type: WORKSPACE_ACTIONS.SET_FETCH_EVALUATIONS, payload: bootData.evaluationsFetch });
  }

  const mergedRosters = { ...existingRosters };
  bootData.rosterUpdates.forEach(({ schoolId, branchId, students }) => {
    const key = rosterKey(schoolId, branchId);
    const existing = mergedRosters[key] || [];
    if (existing.length > 0) return;
    mergedRosters[key] = students;
  });

  dispatch({
    type: WORKSPACE_ACTIONS.BOOT_ENTITIES,
    payload: {
      sessions: bootData.normalizedSessions,
      cohorts: bootData.normalizedCohortEntities,
      evaluations: bootData.normalizedEvaluations,
      rosters: mergedRosters,
    },
  });
}

/**
 * Merge roster students into legacy array (same semantics as App.js prefetch).
 */
export function mergeLegacyRosterStudents(prevStudents, schoolId, branchId, mappedStudents) {
  if (rosterHasStudents(prevStudents, schoolId, branchId)) return prevStudents;
  return mergeRosterStudents(prevStudents, schoolId, branchId, mappedStudents);
}

export function syncWorkspaceSession(dispatch, session) {
  const entity = normalizeSessionEntity(session);
  if (!entity) return;
  dispatch({ type: WORKSPACE_ACTIONS.UPSERT_SESSION, payload: entity });
}

export function syncWorkspaceCohort(dispatch, cohort) {
  const entity = normalizeCohortEntity(cohort);
  if (!entity) return;
  dispatch({ type: WORKSPACE_ACTIONS.UPSERT_COHORT, payload: entity });
}

export function markWorkspaceFetchLoading(dispatch, { loadsEvaluations }) {
  dispatch({
    type: WORKSPACE_ACTIONS.SET_FETCH_COHORTS,
    payload: sliceLoading(makeFetchSlice([])),
  });
  dispatch({
    type: WORKSPACE_ACTIONS.SET_FETCH_SESSIONS,
    payload: sliceLoading(makeFetchSlice([])),
  });
  if (loadsEvaluations) {
    dispatch({
      type: WORKSPACE_ACTIONS.SET_FETCH_EVALUATIONS,
      payload: sliceLoading(makeFetchSlice([])),
    });
  }
}

export function syncWorkspaceRoster(dispatch, schoolId, branchId, students) {
  dispatch({
    type: WORKSPACE_ACTIONS.SET_ROSTER,
    payload: { schoolId, branchId, students },
  });
}
